/** New outbound email from a department mailbox (Collections lead, 9 Oct 2026): To / Cc / Subject / body / attachments,
 *  personal sign-off, optional log on a customer file. `prefill` lets the Customer File open it with the customer's address + file.
 *  To / Cc are token inputs — type a company, borrower or SF number to pick from every address we hold (Dean, Oct 2026). */
import { useMemo, useState } from "react";
import { Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RichTextEditor, textToEditorHtml } from "@/components/RichTextEditor";
import { EmailTemplatesMenu, firstNameOf, insertTemplateHtml } from "@/components/EmailTemplatesMenu";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CasePicker } from "@/pages/inbox/CasePicker";
import { SignoffHint } from "@/pages/inbox/InboxActions";
import { EMAIL_RE, RecipientInput } from "@/pages/inbox/RecipientInput";
import { AttachmentsBox } from "@/components/mail/AttachmentsBox";
import { buildAttachmentsPayload, hasInlineImage, mergeItems, splitInlineImages, uploadItem } from "@/components/mail/attachments";
import { caseFileSource, caseScope } from "@/components/mail/fileSources";

const toTokens = (s) => (s || "").split(/[;,\s]+/).map((a) => a.trim().toLowerCase()).filter(Boolean).map((email) => ({ email, name: "", raw: true, valid: EMAIL_RE.test(email) }));

export function DeptComposeDialog({ api, me, fromLabel, caseLink, prefill = {}, onClose, onSent }) {
  const [to, setTo] = useState(() => toTokens(prefill.to));
  const [cc, setCc] = useState([]);
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState(prefill.subject || "");
  const [body, setBody] = useState(prefill.body || "");
  const [bodyHtml, setBodyHtml] = useState(() => textToEditorHtml(prefill.body || ""));
  const [logOn, setLogOn] = useState(!!prefill.case);
  const [picked, setPicked] = useState(prefill.case || null);
  const [files, setFiles] = useState(() => prefill.files || []);
  const [progress, setProgress] = useState(null);
  const [sending, setSending] = useState(false);
  const fileSource = useMemo(() => caseFileSource(api), [api]);
  const badAddr = [...to, ...cc].find((a) => a.valid === false || !EMAIL_RE.test(a.email));
  const canSend = to.length > 0 && !badAddr && subject.trim() && (body.trim() || hasInlineImage(bodyHtml)) && (!logOn || picked);

  // picking a contact that belongs to a customer file suggests logging the email on that file
  const pickCase = (c) => { if (!picked) { setPicked({ ...c, how: "search" }); setLogOn(true); } };

  const addFiles = (list) => {
    const next = mergeItems(files, Array.from(list || []).map(uploadItem));
    if (!next) { toast.error("Attachments must total under 25 MB"); return; }
    setFiles(next);
  };

  const submit = async () => {
    setSending(true);
    try {
      const { html: sendHtml, inline } = splitInlineImages(bodyHtml);
      const attachments = await buildAttachmentsPayload(files, api.uploadChunk, setProgress, inline);
      const toList = to.map((t) => t.email), ccList = cc.map((t) => t.email);
      const r = await api.compose({ to: toList, cc: ccList, subject: subject.trim(), body, body_html: sendHtml, case_id: logOn && picked ? picked.case_id : "", actor: me?.name || me?.email || "", attachments });
      (r.warnings || []).forEach((w) => toast.warning(w));
      toast.success(`Email sent to ${toList.join(", ")}${r.attachments ? ` with ${r.attachments} attachment${r.attachments === 1 ? "" : "s"}` : ""}${r.filed ? ` · logged on ${r.filed.sf || r.filed.customer_name}` : ""}`);
      onSent?.(r);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setSending(false); setProgress(null); }
  };

  const tryClose = () => { if (sending) return; if (body.trim() || hasInlineImage(bodyHtml) || subject.trim() || files.length) { if (!window.confirm("Discard this email? What you've typed will be lost.")) return; } onClose(); };

  return (
    <Dialog open onOpenChange={(v) => !v && tryClose()}>
      <DialogContent className="max-w-4xl max-h-[94vh] overflow-y-auto" aria-describedby={undefined} data-testid="compose-dialog"
        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}>
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Mail className="w-4 h-4 text-blue-600" />New email
            <span className="text-[11px] font-normal text-muted-foreground">from {fromLabel || `${api.dept}@`}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2.5">
          <div className="flex items-start gap-2">
            <span className="text-xs w-12 text-muted-foreground pt-2">To</span>
            <RecipientInput api={api} value={to} onChange={setTo} onPickCase={pickCase} autoFocus testid="compose-to"
              placeholder="Type a company, borrower, SF number or an email address" />
            {!showCc && <Button type="button" variant="ghost" size="sm" className="h-8 text-[11px]" onClick={() => setShowCc(true)} data-testid="compose-show-cc">Cc</Button>}
          </div>
          {showCc && (
            <div className="flex items-start gap-2">
              <span className="text-xs w-12 text-muted-foreground pt-2">Cc</span>
              <RecipientInput api={api} value={cc} onChange={setCc} testid="compose-cc" placeholder="optional" />
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-xs w-12 text-muted-foreground">Subject</span>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className="h-8 text-sm" data-testid="compose-subject" />
          </div>
          {badAddr && <p className="text-[11px] text-rose-700" data-testid="compose-bad-address">"{badAddr.email}" doesn't look like an email address</p>}
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-slate-600">Message</span>
            <EmailTemplatesMenu dept={api.dept} region={api.region} testId="compose-templates"
              context={{ first_name: firstNameOf(to[0]?.name) || firstNameOf(picked?.director_name), customer_name: picked?.customer_name || to[0]?.name || "", sf_number: picked?.sf || "", my_name: me?.name || "" }}
              getCurrent={() => ({ body_html: bodyHtml, body_text: body, subject })}
              onInsert={({ body_html, subject: tSubject }) => { setBodyHtml((cur) => insertTemplateHtml(cur, body_html)); if (tSubject && !subject.trim()) setSubject(tSubject); }} />
          </div>
          <RichTextEditor value={bodyHtml} onChange={({ html, text }) => { setBodyHtml(html); setBody(text); }} minHeight={240} testId="compose-body" images
            placeholder="Type your email — your personal sign-off is added underneath automatically" />
          <SignoffHint />
          <AttachmentsBox items={files} onChange={setFiles} disabled={sending} progress={progress} fileSource={fileSource} defaultScope={caseScope(picked)} testPrefix="compose"
            onScopePicked={(s) => { if (!picked && s?.case_id) { setPicked({ case_id: s.case_id, sf: s.sf, customer_name: s.customer_name, how: "search" }); setLogOn(true); } }} />
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={logOn} onChange={(e) => setLogOn(e.target.checked)} data-testid="compose-log-toggle" />
            Log this email on a customer file timeline
          </label>
          {logOn && <div className="border rounded p-2 bg-indigo-50/40" data-testid="compose-case-section"><CasePicker api={api} value={picked} onChange={setPicked} suggested={prefill.case || null} caseLink={caseLink} /></div>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={tryClose} disabled={sending}>Cancel</Button>
          <Button onClick={submit} disabled={sending || !canSend} data-testid="compose-submit">
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            {sending ? (progress ? "Uploading…" : "Sending…") : logOn && picked ? `Send & log on ${picked.sf || picked.customer_name}` : "Send email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
